import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { JhHttpClientCached } from '@jack-henry/frontend-utils/http';
import type { CredentialDto, UserModelDto } from '@treasury/api/channel';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { TmHttpClient } from '@treasury/core/http';
import { NavigationService } from '@treasury/core/navigation';
import { BoAccountService } from '@treasury/domain/backoffice/services';
import { State } from '@treasury/domain/backoffice/services/account/constants';
import { StatusType } from '@treasury/domain/channel/mappings/security';
import { SecureTokenActionType } from '@treasury/domain/channel/services/account';
import { ChannelAuthenticationService } from '@treasury/domain/channel/services/account/authentication-service';
import { StatusCode } from '@treasury/domain/channel/services/login';
import { AuthenticationService, SetUserPayload } from '@treasury/domain/services/authentication';
import { IdentityVerificationDialog } from '@treasury/omega/domain-components/identity-verification-dialog';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import { exists } from '@treasury/utils';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { UserOptionViewModel } from './components/user-selection';
import { LoginPhases } from './data/login-phases';
import { LoginClient } from './login-client';

import './components/change-password';
import './components/login';
import './components/login-locked';
import './components/oob-registration';
import './components/partials/login-layout';
import './components/secure-token-registration';
import './components/security-questions';
import './components/security-questions-enrollment';
// eslint-disable-next-line no-duplicate-imports
import './components/user-selection';
import './components/user-verification';

interface Credentials {
    institution: string;
    companyId: string;
    loginId: string;
    password: string;
}

interface RegisterTokenPayload {
    credentialId?: string;
    token?: string;
    pin?: string;
    actionType: SecureTokenActionType;
}

type LoginEvent = CustomEvent<{
    companyId: string;
    loginId: string;
    password: string;
    institutionId?: string;
}>;

const genericErrorMessage =
    'We were unable to process your request at this time. Please try again in a moment.';

export const tagName = 'login-container';
@customElement(tagName)
export class LoginContainer extends TmBaseComponent {
    constructor() {
        super();
        Object.keys(sessionStorage).forEach(key => {
            sessionStorage.removeItem(key);
        });
    }

    @property({ reflect: true })
    public statusCode? = StatusCode.Login;

    @property({
        type: Boolean,
    })
    public loading = false;

    @InjectProperty()
    declare public readonly client: LoginClient;

    public institutionId = '';

    public userOptions: UserOptionViewModel[] = [];

    public loginResult?: CredentialDto;

    public daysUntilPasswordExpires?: number;

    private verifiedUserRecord?: object;

    private verifiedUserResponse: CredentialDto = {
        daysUntilPasswordExpires: 0,
        numberOfSecurityQuestions: 0,
    };

    @state()
    private errorMessage?: string = '';

    @InjectProperty()
    declare private readonly boAccountService: BoAccountService;

    @InjectProperty()
    declare private readonly authService: AuthenticationService;

    @InjectProperty()
    declare private readonly channelAuthService: ChannelAuthenticationService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    private nonSecureCredentials?: { companyId: string; loginId: string };

    private readonly brandedBackgroundPhases: StatusCode[] = [
        StatusCode.Login,
        StatusCode.Locked,
        StatusCode.Loading,
        StatusCode.ChallengeQuestions,
        StatusCode.ResetPassword,
        StatusCode.ResetPasswordEnrollment,
        StatusCode.ChallengeResetPassword,
        StatusCode.ChallengeResetExpiredPassword,
        StatusCode.ResetExpiredPassword,
    ];

    private readonly topAffixedPhases: StatusCode[] = [StatusCode.Locked, StatusCode.Loading];

    private get hasLoginCode() {
        return !!new URLSearchParams(window.location.search).get('code');
    }

    private set hasLoginCode(value) {
        this.hasLoginCode = value;
    }

    private get isBackOffice() {
        return this.config.app === AppType.BackOffice;
    }

    private get isConsumer() {
        return this.config.app !== AppType.BackOffice;
    }

    public connectedCallback() {
        super.connectedCallback();
        this.addEventListener('loadingStatusChange', this.loadingStatusChange as EventListener);
    }

    public disconnectedCallback() {
        this.removeEventListener('loadingStatusChange', this.loadingStatusChange as EventListener);
    }

    public async login(e: LoginEvent) {
        this.loading = true;
        try {
            const { companyId, loginId, password, institutionId } = e.detail;
            const hasInstitutionId = exists(institutionId) && institutionId.trim() !== '';
            this.institutionId = hasInstitutionId ? institutionId : this.config.institutionId;
            const credentials: Credentials = {
                institution: this.institutionId,
                companyId,
                loginId,
                password,
            };
            this.nonSecureCredentials = {
                companyId,
                loginId,
            };

            if (this.config.app === 'BackOffice') {
                await this.authenticateToBackOfficeApi(credentials);
            } else {
                await this.authenticateToConsumerApi(credentials);
            }
            this.authService.setUser(this.nonSecureCredentials as SetUserPayload);
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : genericErrorMessage;
        } finally {
            this.loading = false;
        }
    }

    public async verifySecurityQuestions(e: CustomEvent) {
        this.loading = true;
        try {
            if (this.isConsumer) {
                await this.verifySecurityQuestionsForConsumer(e);
            } else {
                await this.verifySecurityQuestionsForBackOffice(e);
            }
        } catch (err) {
            this.notificationService.renderError(err as Error);
        } finally {
            this.loading = false;
        }
    }

    public async submitUserVerificationForm({
        detail,
    }: {
        detail: { formValues: UserModelDto; record: object };
    }) {
        try {
            this.loading = true;
            const {
                statusCode,
                message,
                data,
                daysUntilPasswordExpires,
                numberOfSecurityQuestions,
            } = await this.client.verifyUser(detail.formValues);
            if (statusCode === StatusCode.Invalid && message) {
                this.notificationService.renderError(message);
                return;
            }
            this.verifiedUserRecord = detail.record;
            this.verifiedUserResponse = {
                data,
                daysUntilPasswordExpires,
                numberOfSecurityQuestions,
            };
            this.statusCode = await this.channelAuthService.goToNextStep();
        } catch (err) {
            this.notificationService.renderError(err as Error);
        } finally {
            this.loading = false;
        }
    }

    public forgotPassword(user: UserModelDto) {
        this.client.forgotPassword(user);
    }

    public async registerSecureToken(e: CustomEvent<RegisterTokenPayload>) {
        const secureTokenDetails = e.detail;
        this.loading = true;
        try {
            const { actionType, credentialId, token, pin } = secureTokenDetails;
            const { success } = await this.client.registerSecureToken(
                actionType,
                credentialId,
                token,
                pin
            );
            if (success) {
                this.errorMessage = '';
                this.statusCode = await this.channelAuthService.goToNextStep();
            } else {
                this.errorMessage = 'There was an error in your entry. Please try again.';
            }
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : genericErrorMessage;
        } finally {
            this.loading = false;
        }
    }

    public async completeOutOfBandConfig() {
        await this.client.completeOutOfBandConfig();
        this.completeLogin();
    }

    public async userSelected(userCompanyUniqueId: string) {
        this.loading = true;
        try {
            const { message } =
                await this.authService.authenticateSelectedCompanyUser(userCompanyUniqueId);

            this.statusCode = await this.channelAuthService.startAuthWorkflow({
                statusCode: StatusCode.Allow,
                message,
                numberOfSecurityQuestions: 0,
                daysUntilPasswordExpires: 0,
            });
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : genericErrorMessage;
        } finally {
            this.loading = false;
        }
    }

    public async saveSecurityQuestions(e: CustomEvent) {
        this.loading = true;
        try {
            const questions = e.detail;
            const success = await this.client.saveSecurityQuestions(questions);
            if (success) {
                this.statusCode = await this.channelAuthService.goToNextStep();
            }
        } catch (err) {
            this.notificationService.renderError(err as Error);
        } finally {
            this.loading = false;
        }
    }

    public async completeLogin() {
        this.statusCode = await this.channelAuthService.goToNextStep();
    }

    public async updatePassword(e: CustomEvent) {
        this.loading = true;
        try {
            if (!this.nonSecureCredentials) {
                throw new Error('Could not update password. Credentials were not available.');
            }

            const { companyId, loginId } = this.nonSecureCredentials;
            const formData: UserModelDto = {
                companyID: companyId,
                loginID: loginId,
                institution: this.config.institutionId,
                password: e.detail,
                useClientBiometricsAuthentication: false,
                id: 0,
            };

            this.loading = true;

            if (this.isConsumer) {
                await this.client.updatePassword(formData);
                this.statusCode =
                    (await this.channelAuthService.goToNextStep()) || StatusCode.Unknown;
            } else {
                const { loginID, password, institution: fiId } = formData;
                if (!loginID || !password || !fiId) {
                    throw new Error('Could not update password; missing data.');
                }

                await this.boAccountService.updatePassword({
                    loginId: loginID,
                    password,
                    confirmPassword: password,
                    fiId,
                });
                this.statusCode = (await this.boAccountService.getNextStep()) || StatusCode.Unknown;
            }
        } catch (err) {
            this.notificationService.renderError(
                err instanceof Error ? err : 'Could not update password. Please try again.'
            );
        } finally {
            this.loading = false;
        }
    }

    protected async firstUpdated() {
        const http = await TmHttpClient.getInstance();
        if (!(http instanceof JhHttpClientCached)) {
            throw new Error('Cannot clear HTTP cache. The client does not implement caching.');
        }

        http.clearCache();

        /* Close any notifications that may have been open on either session timeout or manual logout */
        document
            .querySelectorAll('vaadin-notification')
            .forEach(notification => notification.close());
    }

    protected render() {
        if (!this.statusCode) {
            return nothing;
        }

        if (LoginPhases[this.statusCode]?.isDialog) {
            return this.renderPhase();
        }
        return html`
            ${this.renderBlockingLoader()}
            <login-layout
                .headerText=${this.getHeaderText() || ''}
                .stickyHeader=${!this.brandedBackgroundPhases.includes(this.statusCode)}
                .bottomAffixed=${!this.topAffixedPhases.includes(this.statusCode)}
            >
                <span slot="phase"> ${this.renderErrorMessage()} ${this.renderPhase()}</span>
            </login-layout>
        `;
    }

    private loadingStatusChange(e: CustomEvent) {
        this.loading = e.detail;
    }

    private async authenticateToConsumerApi(credentials: Credentials) {
        this.loginResult = await this.client.login(credentials);
        const { statusCode, message, daysUntilPasswordExpires } = this.loginResult;
        this.daysUntilPasswordExpires = daysUntilPasswordExpires;
        if (statusCode === StatusCode.Invalid) {
            this.errorMessage = message;
            return;
        }

        if (statusCode === StatusCode.Locked) {
            this.statusCode = StatusCode.Login;
            this.errorMessage = 'Your account is locked. Please contact support.';
            return;
        }
        this.statusCode = await this.channelAuthService.startAuthWorkflow(this.loginResult);
        this.errorMessage = '';
    }

    private async authenticateToBackOfficeApi(credentials: Credentials) {
        try {
            const result = await this.boAccountService.login(
                credentials.institution,
                credentials.loginId,
                credentials.password
            );

            const { message, userCredentialStatus, rsaStatus, secureTokenStatus, data } = result;
            const status = {
                userCredentialStatus: userCredentialStatus as StatusCode,
                rsaStatus: rsaStatus as StatusCode,
                secureTokenStatus: secureTokenStatus as StatusCode,
            };

            if ([StatusCode.Invalid, StatusCode.Blocked].includes(status.userCredentialStatus)) {
                this.errorMessage = message ?? genericErrorMessage;
                return;
            }

            for (const key of Object.keys(status)) {
                // Get value from key
                const value = status[key as keyof typeof status];
                if (value === StatusCode.Locked) {
                    let lockType = '';
                    if (key !== 'userCredentialStatus') {
                        lockType = key === 'rsaStatus' ? 'via RSA' : 'via Secure Token';
                    }
                    this.statusCode = StatusCode.Locked;

                    this.errorMessage = `Your account is locked ${lockType}. Please contact support.`;
                    return;
                }
            }

            if (status.userCredentialStatus === StatusCode.Locked) {
                this.statusCode = status.userCredentialStatus;
                return;
            }
            const boStatus = {
                userCredentialStatus,
                rsaStatus,
                secureTokenStatus,
            } as State;
            if (exists(credentials.institution)) {
                this.config.institutionId = credentials.institution;
            }
            const statusCode = await this.boAccountService.initiateAuthentication(boStatus);
            if (statusCode === StatusCode.ChallengeQuestions) {
                this.loginResult = {
                    statusCode,
                    message: '',
                    data,
                    daysUntilPasswordExpires: 0,
                    numberOfSecurityQuestions: 0,
                };
            }
            this.statusCode = statusCode;
        } catch (e) {
            this.errorMessage = e instanceof Error ? e.message : genericErrorMessage;
        } finally {
            this.loading = false;
        }
    }

    private async validateOneTimePassword(
        { oneTimePassword }: { oneTimePassword: string },
        dialog: IdentityVerificationDialog
    ) {
        try {
            const result = await this.client.validateOneTimePassword(oneTimePassword);
            if (result.status === StatusType.Success) {
                dialog.cancel();
                this.checkTermsAndConditions();
            } else {
                dialog.securityMessage = {
                    ...dialog.securityMessage,
                    status: StatusType.Failure,
                };
            }
        } catch (e) {
            dialog.securityMessage = {
                ...dialog.securityMessage,
                status: StatusType.Failure,
            };
            this.errorMessage =
                e instanceof Error ? e.message : 'We were unable to validate your password.';
        } finally {
            dialog.verifying = false;
        }
    }

    private getHeaderText() {
        if (!this.statusCode) {
            return '';
        }

        return LoginPhases[this.statusCode]?.title;
    }

    private async checkTermsAndConditions() {
        const { isComplete } = await this.client.checkLoginCompletion();
        const result = await this.client.getTermsAndConditions();

        if (!isComplete && result && result.text) {
            this.navService.navigate(`/terms-and-conditions-accept`);
        } else {
            sessionStorage.setItem('termsAndConditions', 'true');
            this.navService.navigate(`/dashboard`);
        }
    }

    private async verifySecurityQuestionsForConsumer(e: CustomEvent) {
        const response = await this.client.verifySecurityQuestions(e.detail);
        if (!response.statusCode || response.statusCode === 'Undefined') {
            throw new Error(response.message);
        } else {
            this.statusCode = await this.channelAuthService.goToNextStep();
            this.errorMessage = '';
        }
    }

    private async verifySecurityQuestionsForBackOffice(e: CustomEvent) {
        const response = await this.boAccountService.verifySecurityQuestions(e.detail);
        if (!response.statusCode || response.statusCode === 'Undefined') {
            throw new Error(response.message);
        } else {
            this.statusCode = await this.boAccountService.getNextStep();
            this.errorMessage = '';
        }
    }

    private renderErrorMessage() {
        if (!this.errorMessage) {
            return nothing;
        }
        return html`
            <div class="error">
                <omega-icon icon="times-circle"></omega-icon>
                <span class="error-message ml-3 text-sm">${this.errorMessage}</span>
            </div>
        `;
    }

    private renderPhase() {
        if (!this.statusCode) {
            return nothing;
        }

        return LoginPhases[this.statusCode]?.render(this);
    }

    private renderBlockingLoader() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return nothing;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                    overflow: hidden;
                    height: 100%;
                    color: var(--login-text-color, #000);
                }

                :host([theme='dark']) {
                    color: var(--login-text-color, #fff);
                }
                .error omega-icon {
                    color: var(--content-attention);
                }
                .error {
                    border: 1px solid var(--content-attention);
                    border-left: 8px solid var(--content-attention);
                    border-radius: 4px;
                    padding: 7px 15px;
                    margin: 15px;
                    display: flex;
                    align-items: center;
                    color: var(--primary-text-color);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoginContainer;
    }
}
